import { BookOpen, ExternalLink, GraduationCap, Award, Mic, Building2, ArrowRight, Star } from "lucide-react";
import { Button } from "./ui/button";
import jeffPhoto from "../assets/jeff-hastings.png";

const AUTHOR_PHOTO = jeffPhoto;

const AMAZON_AUTHOR = "https://www.amazon.com/stores/Jeff-Hastings/author/B00MAFU684";

const BOOKS = [
  {
    title: "So You Want to Be an Insurance Agent",
    subtitle: "Building a High-Touch Agency in a High-Tech World — 4th Edition",
    desc: "Jeff's signature insurance book, updated for today's industry. Drawing on decades of recruiting, training, and coaching agency owners, it delivers a complete system for licensing, business planning, hiring, and growth — and shows how technology is reshaping the modern agency.",
    rating: "4.7",
    url: "https://www.amazon.com/You-Want-Insurance-Agent-High-Touch-ebook/dp/B0CMJWK6PV",
  },
  {
    title: "The Most Successful Failure in the World",
    subtitle: "Building a Business That Gives Life Meaning",
    desc: "A moving business parable about entrepreneurship, success, family, and priorities. Through the story of driven owner Alex Moss, Jeff shares ten principles for building a company that enhances your life instead of consuming it.",
    rating: "4.5",
    url: "https://www.amazon.com/Most-Successful-Failure-World-Building-ebook/dp/B00SX7CPC8",
  },
  {
    title: "From Campus to Career",
    subtitle: "Co-authored with Maxwell V. Sweet",
    desc: "A step-by-step guide helping high school and college students earn better grades, choose the right major and career, master study habits and networking, and transition confidently from school into professional life.",
    rating: "5.0",
    url: "https://www.amazon.com/Campus-Career-Step-Step-Brighter-ebook/dp/B0F8RWBJW2",
  },
];

const HIGHLIGHTS = [
  { icon: Building2, text: "Farmers Insurance agency owner, recruiter, trainer & District Manager since 1985" },
  { icon: GraduationCap, text: "M.A. Industrial-Organizational Psychology, Harvard Extension School" },
  { icon: Award, text: "Adjunct Excellence Award & Faculty Fellow, Lone Star College (2026)" },
  { icon: Mic, text: "Host of the Psychology for Students podcast" },
];

export default function AuthorSection() {
  return (
    <section id="author" className="py-20 sm:py-24 bg-white border-t border-slate-100" data-testid="author-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <p className="font-mono text-xs uppercase tracking-widest text-amber-600 mb-3">About the Author &amp; Books</p>

        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-10 lg:gap-14">
          {/* LEFT — bio */}
          <div className="order-2 lg:order-1">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">Jeff Hastings, ALM, LUTCF</h2>
            <p className="mt-2 text-sm font-medium text-amber-700">
              Insurance Executive · Author · Educator · Adjunct Professor of Psychology
            </p>

            <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
              <p>
                Jeff Hastings' insurance career began with <span className="font-semibold text-[#0A192F]">Farmers Insurance in 1985</span>,
                when he started as a file clerk and worked his way up to become an agency owner, business leader, recruiter, trainer, and
                District Manager. Along the way he built one of the company's larger agency organizations and earned national recognition
                for recruiting, growth, production, and profitability — including District Manager of the Year and multiple Presidents Council awards.
              </p>
              <p>
                Today Jeff again works directly with Farmers agencies, helping recruit, develop, and mentor future agency owners across
                California and Texas. That hands-on involvement keeps him connected to the real challenges new insurance professionals face —
                from getting licensed to building a successful career after passing the exam.
              </p>
              <p>
                He is also President and CEO of <span className="font-semibold text-[#0A192F]">Ivy Consulting Group</span>, where his work
                focuses on leadership, recruiting, training, and the application of psychology to the workplace. Jeff holds a master's degree
                in Industrial-Organizational Psychology from <span className="font-semibold text-[#0A192F]">Harvard Extension School</span>,
                bringing an academic understanding of learning, motivation, and memory to decades of practical business experience.
              </p>
            </div>

            {/* Highlights */}
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {HIGHLIGHTS.map((h, i) => (
                <div key={h.text} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5" data-testid={`author-highlight-${i}`}>
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#0A192F]">
                    <h.icon className="h-4 w-4 text-amber-500" />
                  </span>
                  <span className="text-sm text-slate-700 leading-snug">{h.text}</span>
                </div>
              ))}
            </div>

            {/* Professor */}
            <h3 className="mt-10 font-heading text-xl font-bold text-[#0A192F]">From insurance professional to professor</h3>
            <div className="mt-3 space-y-4 text-slate-600 leading-relaxed">
              <p>
                Jeff serves as an <span className="font-semibold text-[#0A192F]">Adjunct Professor of Psychology at Lone Star College</span>,
                where his teaching focuses not on memorizing information, but on helping students understand it, apply it, and remember it.
                In 2026 the college recognized him with an Adjunct Excellence Award and selected him as a Faculty Fellow supporting
                evidence-based approaches to student learning.
              </p>
              <p>That experience shaped the philosophy behind Insurance License Lab.</p>
            </div>

            {/* Philosophy callout */}
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6">
              <p className="text-[#0A192F] leading-relaxed">
                Passing a licensing exam shouldn't require endlessly rereading a textbook or memorizing hundreds of disconnected facts.
                Insurance License Lab combines written instruction, professional audio, active recall, practice questions, repetition, and
                exam simulation so you learn the material in multiple ways.
              </p>
              <p className="mt-3 font-heading font-semibold text-[#0A192F]">
                The goal isn't to help you study more — it's to help you study smarter, and get licensed.
              </p>
            </div>

            {/* Why he created it */}
            <h3 className="mt-10 font-heading text-xl font-bold text-[#0A192F]">Why Jeff created Insurance License Lab</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              After decades of recruiting and training insurance professionals, Jeff kept seeing the same problem: smart, capable people
              struggle with licensing exams because traditional prep emphasizes memorization instead of learning. Insurance License Lab was
              built around a simple idea — people learn better when they <em>read it, hear it, retrieve it from memory, test themselves on it,
              and revisit difficult material over time</em>. The result is an exam-prep system designed around the learner, not just the textbook.
            </p>
          </div>

          {/* RIGHT — photo + books */}
          <div className="order-1 lg:order-2">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="relative">
                <div className="absolute -inset-3 bg-[#0A192F]/5 rounded-3xl blur-2xl" />
                {AUTHOR_PHOTO ? (
                  <img
                    src={AUTHOR_PHOTO}
                    alt="Jeff Hastings"
                    className="relative rounded-2xl shadow-xl w-full object-cover object-top aspect-[4/5] bg-white ring-1 ring-slate-200"
                    data-testid="author-photo"
                  />
                ) : (
                  <div
                    className="relative rounded-2xl shadow-xl w-full aspect-[4/5] bg-gradient-to-br from-[#0A192F] to-[#122a4d] flex flex-col items-center justify-center text-center px-6"
                    data-testid="author-photo-placeholder"
                  >
                    <div className="h-24 w-24 rounded-full bg-white/10 flex items-center justify-center ring-1 ring-white/20">
                      <span className="font-heading text-3xl font-bold text-amber-500">JH</span>
                    </div>
                    <p className="mt-5 font-heading font-semibold text-white">Jeff Hastings</p>
                    <p className="mt-1 text-xs font-mono uppercase tracking-widest text-amber-500">Author · Educator</p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-heading text-lg font-bold text-[#0A192F] flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-amber-600" /> Books by Jeff Hastings
                </h3>
                <div className="mt-4 space-y-3">
                  {BOOKS.map((b, i) => (
                    <a
                      key={b.title}
                      href={b.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group block rounded-xl border border-slate-200 p-4 hover:border-amber-400 hover:shadow-md transition"
                      data-testid={`author-book-${i}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="font-semibold text-[#0A192F] leading-snug">{b.title}</span>
                        <ExternalLink className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-amber-600 transition" />
                      </div>
                      <p className="mt-0.5 text-xs font-medium text-amber-700">{b.subtitle}</p>
                      <div className="mt-1.5 flex items-center gap-1 text-xs text-slate-500">
                        <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                        <span className="font-semibold text-[#0A192F]">{b.rating}</span> rating on Amazon
                      </div>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                    </a>
                  ))}
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  Earlier editions of <span className="font-medium text-slate-600">So You Want to Be an Insurance Agent</span> (3rd &amp; 2nd editions) are also available on Amazon.
                </p>
                <Button
                  asChild
                  className="mt-4 w-full bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="author-amazon-btn"
                >
                  <a href={AMAZON_AUTHOR} target="_blank" rel="noopener noreferrer">
                    View Jeff's books on Amazon <ArrowRight className="h-4 w-4 ml-1" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
